import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ConfirmDialogContext = createContext(null);

function ConfirmDialog({
  isOpen,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmVariant,
  onCancel,
  onConfirm
}) {
  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="confirm-dialog" role="presentation">
      <button className="confirm-dialog__backdrop" type="button" aria-label="Luk bekræftelse" onClick={onCancel} />
      <section
        className="confirm-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className="confirm-dialog__icon" aria-hidden="true">
          <i className="bi bi-exclamation-circle" />
        </div>
        <h2 className="confirm-dialog__title" id="confirm-dialog-title">{title}</h2>
        <p className="confirm-dialog__description">{description}</p>
        <div className="confirm-dialog__actions">
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className={`btn btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}

export function ConfirmDialogProvider({ children }) {
  const resolverRef = useRef(null);
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmLabel: "Bekræft",
    cancelLabel: "Annuller",
    confirmVariant: "primary"
  });

  const closeDialog = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }

    setDialogState({
      isOpen: false,
      title: "",
      description: "",
      confirmLabel: "Bekræft",
      cancelLabel: "Annuller",
      confirmVariant: "primary"
    });
  }, []);

  const confirm = useCallback((options) => (
    new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialogState({
        isOpen: true,
        title: options.title || "Er du sikker?",
        description: options.description || "",
        confirmLabel: options.confirmLabel || "Bekræft",
        cancelLabel: options.cancelLabel || "Annuller",
        confirmVariant: options.confirmVariant || "primary"
      });
    })
  ), []);

  const contextValue = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        description={dialogState.description}
        confirmLabel={dialogState.confirmLabel}
        cancelLabel={dialogState.cancelLabel}
        confirmVariant={dialogState.confirmVariant}
        onCancel={() => closeDialog(false)}
        onConfirm={() => closeDialog(true)}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);

  if (!context) {
    throw new Error("useConfirmDialog must be used within ConfirmDialogProvider");
  }

  return context;
}
